import { TFunction } from 'i18next'
import z from 'zod'

export const ITEM_NAME_MAX_LENGTH = 29
export const ITEM_DESCRIPTION_MAX_LENGTH = 292
export const PRODUCT_IMAGE_ACCEPTED_TYPES = ['.png', '.jpg', '.jpeg']

export const createMarketShopProductSchema = (
  t: TFunction<['market-shop-dashboard']>,
  discountTagId?: number,
) =>
  z
    .object({
      buildingId: z.number(),
      categoryId: z.union([z.number(), z.literal('all')]).optional(),
      description: z.string().max(
        ITEM_DESCRIPTION_MAX_LENGTH,
        t('market-shop-dashboard:product.description.validation.maxLength', {
          max: ITEM_DESCRIPTION_MAX_LENGTH,
        }),
      ),
      discount: z.string(),
      image: z.array(z.string()),
      price: z.string().min(1, t('market-shop-dashboard:product.price.validation.required')),
      quantity: z.string(),
      summary: z
        .string()
        .min(1, t('market-shop-dashboard:product.summary.validation.required'))
        .max(
          ITEM_NAME_MAX_LENGTH,
          t('market-shop-dashboard:product.summary.validation.maxLength', {
            max: ITEM_NAME_MAX_LENGTH,
          }),
        ),
      tags: z.array(z.number()),
      tax: z
        .string()
        .refine(
          (val) => val === '' || (Number(val) >= 0 && Number(val) <= 100),
          t('market-shop-dashboard:product.tax.validation.range'),
        ),
      unlimited: z.boolean(),
    })
    .superRefine((data, ctx) => {
      if (!data.unlimited && !data.quantity) {
        ctx.addIssue({
          code: 'custom',
          message: t('market-shop-dashboard:product.quantity.validation.required'),
          path: ['quantity'],
        })
      }

      if (discountTagId !== undefined && data.tags.includes(discountTagId)) {
        if (!data.discount) {
          ctx.addIssue({
            code: 'custom',
            message: t('market-shop-dashboard:product.discount.validation.required'),
            path: ['discount'],
          })
        } else {
          const discountNum = Number(data.discount)
          if (isNaN(discountNum) || discountNum <= 0 || discountNum > 99) {
            ctx.addIssue({
              code: 'custom',
              message: t('market-shop-dashboard:product.discount.validation.range'),
              path: ['discount'],
            })
          }
        }
      }
    })

export type MarketShopProductFormData = z.infer<ReturnType<typeof createMarketShopProductSchema>>

export const DEFAULT_MARKET_SHOP_PRODUCT_FORM_DATA: MarketShopProductFormData = {
  buildingId: 0,
  categoryId: undefined,
  description: '',
  discount: '',
  image: [],
  price: '',
  quantity: '',
  summary: '',
  tags: [],
  tax: '',
  unlimited: false,
}
